import { useRef, useState } from "react";
import { Button, Input as AntInput } from "antd";
import { Input } from "@components/global/new/Input/Input";
import styles from "./ContactUsCard.module.scss";
import { useIntl } from "react-intl";
import ReCAPTCHA from "react-google-recaptcha";
import cn from "classnames";
import { IS_DEBUG, REACT_RECAPTCH_KEY } from "../../../../config";
import { wlpFormsApi } from "../../utils";
import useMediaQuery from "@utils/hooks/utils";
import { useWlpFormStatus } from "../../hooks/useWlpFormStatus";
import { WlpFormStatusIcon } from "../WlpFormStatusIcon/WlpFormStatusIcon";

interface ContactElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  message: HTMLTextAreaElement;
}

interface ContactForm extends HTMLFormElement {
  readonly elements: ContactElements;
}

interface ContactErrors {
  email?: string;
  message?: string;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ContactUsCard() {
  const { formatMessage } = useIntl();
  const recaptchaRef = useRef<any>(null);
  const isXs = useMediaQuery("max", 574);
  const isNarrowPhone = useMediaQuery("max", 370);
  const [recaptchaResponse, setRecaptchaResponse] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ContactErrors>({});
  const formStatus = useWlpFormStatus();

  const clearEmailError = () => {
    formStatus.clearStatus();
    setErrors((prev) => {
      if (!prev.email) return prev;
      return { ...prev, email: undefined };
    });
  };

  const clearMessageError = () => {
    formStatus.clearStatus();
    setErrors((prev) => {
      if (!prev.message) return prev;
      return { ...prev, message: undefined };
    });
  };

  const onSubmit = async (e: React.FormEvent<ContactForm>) => {
    e.preventDefault();
    if (loading) return;
    formStatus.clearStatus();

    const form = e.currentTarget;
    const email = form.elements.email.value.trim();
    const message = form.elements.message.value.trim();
    const recaptcha = IS_DEBUG ? "fakerecaptcha" : recaptchaResponse;
    const nextErrors: ContactErrors = {};

    if (!email) {
      nextErrors.email = formatMessage({ id: "wlp.form.emailRequired" });
    }

    if (email && !emailPattern.test(email)) {
      nextErrors.email = formatMessage({ id: "wlp.form.emailInvalid" });
    }

    if (!message) {
      nextErrors.message = formatMessage({ id: "wlp.form.messageRequired" });
    }

    setErrors(nextErrors);

    if (nextErrors.email || nextErrors.message) {
      return;
    }

    if (!recaptcha) {
      formStatus.showError(formatMessage({ id: "wlp.form.recaptchaRequired" }));
      return;
    }

    setLoading(true);

    try {
      const response = await wlpFormsApi.order(
        {
          email,
          message,
        },
        recaptcha
      );

      if (response.Status === "error") {
        formStatus.showError(
          response.Message || formatMessage({ id: "wlp.form.genericError" })
        );
        return;
      }

      form.reset();
      recaptchaRef.current?.reset();
      setRecaptchaResponse(undefined);
      setErrors({});
      formStatus.showSuccess(formatMessage({ id: "wlp.contact.success" }));
    } catch {
      formStatus.showError(formatMessage({ id: "wlp.form.genericError" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.card} id="contact-us">
      <h2 className={styles.title}>{formatMessage({ id: "wlp.contact.title" })}</h2>
      <form className={styles.form} onSubmit={onSubmit}>
        <Input
          label={formatMessage({ id: "wlp.contact.emailLabel" })}
          name="email"
          type="email"
          placeholder={formatMessage({ id: "wlp.contact.emailPlaceholder" })}
          size="large"
          colored
          outlined
          wrapClassName={styles.field}
          onChange={clearEmailError}
        />
        {errors.email && <span className={styles.error}>{errors.email}</span>}
        <label className={styles.textareaWrap}>
          <span>{formatMessage({ id: "wlp.contact.messageLabel" })}</span>
          <AntInput.TextArea
            name="message"
            status={errors.message ? "error" : undefined}
            placeholder={formatMessage({ id: "wlp.contact.messagePlaceholder" })}
            autoSize={{ minRows: 8, maxRows: 8 }}
            onChange={clearMessageError}
          />
        </label>
        {errors.message && <span className={styles.error}>{errors.message}</span>}
        {REACT_RECAPTCH_KEY && !IS_DEBUG && (
          <div
            className={cn("g-recaptcha", styles.recaptcha, {
              [styles.xsRecaptcha]: isXs,
              [styles.compactRecaptcha]: isNarrowPhone,
            })}
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={REACT_RECAPTCH_KEY}
              size={isNarrowPhone ? "compact" : "normal"}
              onChange={(response) => setRecaptchaResponse(response || undefined)}
              onExpired={() => setRecaptchaResponse(undefined)}
            />
          </div>
        )}
        <div className={styles.actions}>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.send}
            loading={loading}
            disabled={REACT_RECAPTCH_KEY && !IS_DEBUG ? !recaptchaResponse : false}
          >
            {formatMessage({ id: "wlp.contact.send" })}
          </Button>
        </div>
        <p
          className={cn(styles.status, {
            [styles.statusVisible]: formStatus.status?.visible,
            [styles.statusSuccess]: formStatus.status?.type === "success",
            [styles.statusError]: formStatus.status?.type === "error",
          })}
          aria-live={formStatus.status?.type === "error" ? "assertive" : "polite"}
        >
          {formStatus.status && <WlpFormStatusIcon type={formStatus.status.type} />}
          <span>{formStatus.status?.message}</span>
        </p>
      </form>
    </section>
  );
}

export { ContactUsCard };
